// internal imports
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDocumentTitle } from "../../../hooks";
import axiosInstance from "../../../shared/axiosInstance";

// Third party
import { DataGrid } from "@mui/x-data-grid";
import { Chip, LinearProgress, Tab, Tabs } from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";

// style
import "../Assignment.scss";
import { HotelPastToolbar } from "./HotelPastToolbar";
import {
  addDays,
  differenceInDays,
  removeSecondsFromDateTime,
} from "../../../utils/date.utils";
import { getUserData } from "../../../shared/utils";
import { API, EHotelServiceRequestType } from "../../../constants";
import { MGButton } from "../../../components";

const TABS = {
  CHECK_INS: "checkIns",
  MEAL_SCANS: "mealScans",
} as const;

type HotelPastTab = (typeof TABS)[keyof typeof TABS];

const MEAL_TYPE_LABELS: Record<string, string> = {
  [EHotelServiceRequestType.BREAKFAST]: "Breakfast",
  [EHotelServiceRequestType.LUNCH]: "Lunch",
  [EHotelServiceRequestType.DINNER]: "Dinner",
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// How many rows land in the browser before the user has to ask for more.
const LOAD_CHUNK_ROWS = 2000;

// Safety valve for the cursor loop: 200 pages x 200 rows = 40k scans, far
// beyond a 15-day window for one vendor.
const MAX_FETCH_PAGES = 200;

interface ITabData {
  rows: any[];
  cursor: unknown; // non-null means the server has more rows in the range
  total: number | null;
}

const EMPTY_TAB_DATA: ITabData = { rows: [], cursor: null, total: null };

type TabDataMap = Record<HotelPastTab, ITabData>;

const EMPTY_TABS_DATA: TabDataMap = {
  [TABS.CHECK_INS]: EMPTY_TAB_DATA,
  [TABS.MEAL_SCANS]: EMPTY_TAB_DATA,
};

const NO_TABS_LOADING: Record<HotelPastTab, boolean> = {
  [TABS.CHECK_INS]: false,
  [TABS.MEAL_SCANS]: false,
};

// Follows the endpoint's cursor until maxRows are gathered or the range is
// exhausted. Each response is one bounded page, so neither server nor request
// ever holds the full set. onPage sees every page as it arrives and can
// return false to stop early (e.g. the user picked a new date range).
const fetchScanPages = async (
  baseUrl: string,
  startCursor: unknown,
  maxRows: number,
  onPage?: (pageRows: any[], cursor: unknown) => boolean | void
): Promise<{ rows: any[]; cursor: unknown }> => {
  const rows: any[] = [];
  let cursor: unknown = startCursor;
  let pages = 0;

  do {
    const url = cursor
      ? `${baseUrl}&cursor=${encodeURIComponent(JSON.stringify(cursor))}`
      : baseUrl;
    const response = await axiosInstance.get(url);
    const body = response.data;

    if (body?.status !== "SUCCESS") {
      throw new Error(body?.message || "Failed to fetch scans");
    }

    const pageRows = Array.isArray(body.data) ? body.data : [];
    rows.push(...pageRows);
    cursor = body.cursor || null;
    pages++;
    if (onPage && onPage(pageRows, cursor) === false) break;
  } while (cursor && rows.length < maxRows && pages < MAX_FETCH_PAGES);

  return { rows, cursor };
};

const formatCount = (value: number) => value.toLocaleString("en-US");

const fetchScanCount = async (baseUrl: string): Promise<number | null> => {
  try {
    const response = await axiosInstance.get(`${baseUrl}&count=true`);
    const total = response.data?.total;
    return typeof total === "number" ? total : null;
  } catch {
    // The summary degrades gracefully; the rows themselves still load.
    return null;
  }
};

const formatScanTimestamp = (value?: string, inputFormat?: string): string => {
  if (!value) return "";
  const parsed = inputFormat ? moment(value, inputFormat) : moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD hh:mm A") : "";
};

// A regular (device-scanned) row needs no admin review, so it counts as
// approved; manual scans carry their actual review state.
const getReviewStatus = (row: any): string =>
  row?.isManuallyAdded
    ? String(row.reviewStatus || "PENDING").toUpperCase()
    : "APPROVED";

const getCheckInFullName = (row: any): string =>
  `${row.FirstName || ""} ${row.LastName || ""}`.trim() ||
  row.TMDetails_Name ||
  "";

const getCheckInDate = (row: any): string =>
  removeSecondsFromDateTime(
    `${row.arrivalDate || ""} ${row.arrivalTime || ""}`.trim()
  );

const getScannedByLabel = (row: any): string =>
  row.isManuallyAdded ? "Manual Scan" : row.vendorEmpName || row.empName || "";

// Export mappings mirror the grid columns so the file matches what users see.
const CHECK_IN_EXPORT_COLUMNS: Array<[string, (row: any) => string]> = [
  ["Crew Id", (row) => row.tmId || ""],
  ["Full name", getCheckInFullName],
  ["Check-in date", getCheckInDate],
  ["Scanned by", getScannedByLabel],
  ["Scanned on", (row) => formatScanTimestamp(row.scannedByGreeterOn)],
  ["Reservation No", (row) => row.ReservationNo || ""],
  ["Review status", getReviewStatus],
];

const MEAL_EXPORT_COLUMNS: Array<[string, (row: any) => string]> = [
  ["Crew Id", (row) => row.tmId || ""],
  ["Full name", (row) => row.TMDetails_Name || ""],
  ["Date", (row) => row.date || ""],
  ["Meal", (row) => MEAL_TYPE_LABELS[row.requestType] || row.requestType || ""],
  [
    "Scanned on",
    (row) => formatScanTimestamp(row.CheckInDate, "MM/DD/YYYY HH:mm:ss"),
  ],
  ["Scanned by", (row) => (row.isManuallyAdded ? "Manual Scan" : row.vendorEmpName || "")],
  ["Review status", getReviewStatus],
];

const escapeCsvValue = (value: string): string =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadCsv = (
  rows: any[],
  columns: Array<[string, (row: any) => string]>,
  fileName: string
) => {
  const lines = [
    columns.map(([header]) => escapeCsvValue(header)).join(","),
    ...rows.map((row) =>
      columns.map(([, getValue]) => escapeCsvValue(getValue(row))).join(",")
    ),
  ];
  const csv = String.fromCharCode(0xfeff) + `${lines.join("\r\n")}\r\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Same chip palette as the invoicing list's Invoice Status column, so status
// pills look identical across the app: filled chips, green for approved,
// grey for pending, red for rejected.
const REVIEW_STATUS_CHIP_COLOR: Record<
  string,
  "default" | "success" | "error"
> = {
  APPROVED: "success",
  PENDING: "default",
  REJECTED: "error",
};

const reviewStatusColumn = {
  field: "reviewStatus",
  headerName: "Review status",
  flex: 1,
  minWidth: 150,
  valueGetter: (value: any, row: any) => getReviewStatus(row),
  renderCell: (params: any) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Chip
        label={params.value}
        color={REVIEW_STATUS_CHIP_COLOR[params.value] || "default"}
      />
    </div>
  ),
};

export const MAX_RANGE_DAYS = 14; // start..end inclusive = 15 days

interface HotelPastProps {
  // Super admin pages pass the vendor they picked; hotel vendors see their own.
  vendorId?: string;
}

export const HotelPast = ({ vendorId: vendorIdProp }: HotelPastProps = {}) => {
  useDocumentTitle("Past scans");

  const [activeTab, setActiveTab] = useState<HotelPastTab>(TABS.CHECK_INS);
  const [searchParams, setSearchParams] = useState("");
  const [reviewStatusFilter, setReviewStatusFilter] = useState("");

  // The pickers edit a draft range; the grid only refetches when the user
  // hits Apply, which copies the draft into the applied range.
  const defaultStart = addDays(new Date(), -7);
  const defaultEnd = addDays(new Date(), -1);
  const [draftStartDate, setDraftStartDate] = useState<Date>(defaultStart);
  const [draftEndDate, setDraftEndDate] = useState<Date>(defaultEnd);
  const [appliedStartDate, setAppliedStartDate] = useState<Date>(defaultStart);
  const [appliedEndDate, setAppliedEndDate] = useState<Date>(defaultEnd);

  const [tabsData, setTabsData] = useState<TabDataMap>(EMPTY_TABS_DATA);
  const [isRowsLoading, setRowsLoading] = useState(false);
  const [loadingTabs, setLoadingTabs] =
    useState<Record<HotelPastTab, boolean>>(NO_TABS_LOADING);
  const [isExporting, setExporting] = useState(false);

  // The ref is the source of truth for async loaders (it is updated
  // synchronously with every page), state only drives rendering.
  const tabsDataRef = useRef<TabDataMap>(EMPTY_TABS_DATA);
  // Bumped on every new date range so loaders still running for the old
  // range stop and throw their pages away.
  const generationRef = useRef(0);
  const inFlightRef = useRef<Record<HotelPastTab, Promise<boolean> | null>>({
    [TABS.CHECK_INS]: null,
    [TABS.MEAL_SCANS]: null,
  });

  const vendorId = vendorIdProp ?? getUserData()["cognito:username"];

  const startDateFormatted = moment(appliedStartDate).format("DD/MM/YYYY");
  const endDateFormatted = moment(appliedEndDate).format("DD/MM/YYYY");

  const rangeParams = `vendorId=${vendorId}&startDate=${startDateFormatted}&endDate=${endDateFormatted}`;
  const checkInUrl = `${API.HOTEL.PAST_CHECKIN_SCANS}?${rangeParams}`;
  const mealUrl = `${API.HOTEL.PAST_MEAL_SCANS}?${rangeParams}`;

  const replaceTabsData = useCallback((next: TabDataMap) => {
    tabsDataRef.current = next;
    setTabsData(next);
  }, []);

  const updateTabData = useCallback(
    (tab: HotelPastTab, update: (prev: ITabData) => ITabData) => {
      replaceTabsData({
        ...tabsDataRef.current,
        [tab]: update(tabsDataRef.current[tab]),
      });
    },
    [replaceTabsData]
  );

  const setTabLoading = useCallback(
    (tab: HotelPastTab, value: boolean) =>
      setLoadingTabs((prev) => ({ ...prev, [tab]: value })),
    []
  );

  const fetchScans = useCallback(async () => {
    const generation = ++generationRef.current;
    inFlightRef.current = {
      [TABS.CHECK_INS]: null,
      [TABS.MEAL_SCANS]: null,
    };
    setLoadingTabs(NO_TABS_LOADING);
    replaceTabsData(EMPTY_TABS_DATA);
    setRowsLoading(true);
    try {
      const [checkIns, meals, checkInTotal, mealTotal] = await Promise.all([
        fetchScanPages(checkInUrl, null, LOAD_CHUNK_ROWS),
        fetchScanPages(mealUrl, null, LOAD_CHUNK_ROWS),
        fetchScanCount(checkInUrl),
        fetchScanCount(mealUrl),
      ]);
      if (generationRef.current !== generation) return;
      replaceTabsData({
        [TABS.CHECK_INS]: { ...checkIns, total: checkInTotal },
        [TABS.MEAL_SCANS]: { ...meals, total: mealTotal },
      });
    } catch (err: any) {
      if (generationRef.current !== generation) return;
      replaceTabsData(EMPTY_TABS_DATA);
      toast.error(err?.message || "Failed to load past scans");
    } finally {
      if (generationRef.current === generation) setRowsLoading(false);
    }
  }, [checkInUrl, mealUrl, replaceTabsData]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  // Loads more of a tab's range in the background, appending each page as it
  // arrives so the grid (and search results) grow while the user waits. Only
  // one loader runs per tab; callers asking while one is running share it.
  // Resolves to false when a request failed (the error is already shown).
  const loadRemaining = useCallback(
    (tab: HotelPastTab, maxRows: number): Promise<boolean> => {
      const existing = inFlightRef.current[tab];
      if (existing) return existing;

      const startCursor = tabsDataRef.current[tab].cursor;
      if (!startCursor) return Promise.resolve(true);

      const generation = generationRef.current;
      const url = tab === TABS.MEAL_SCANS ? mealUrl : checkInUrl;
      setTabLoading(tab, true);

      const promise: Promise<boolean> = fetchScanPages(
        url,
        startCursor,
        maxRows,
        (pageRows, cursor) => {
          if (generationRef.current !== generation) return false;
          updateTabData(tab, (prev) => ({
            ...prev,
            rows: [...prev.rows, ...pageRows],
            cursor,
          }));
        }
      )
        .then(() => true)
        .catch((err: any) => {
          if (generationRef.current === generation) {
            toast.error(err?.message || "Failed to load more scans");
          }
          return false;
        })
        .finally(() => {
          if (inFlightRef.current[tab] === promise) {
            inFlightRef.current[tab] = null;
          }
          if (generationRef.current === generation) setTabLoading(tab, false);
        });

      inFlightRef.current[tab] = promise;
      return promise;
    },
    [checkInUrl, mealUrl, updateTabData, setTabLoading]
  );

  // Pulls every remaining page for a tab, first waiting for a loader that is
  // already running (it may stop at a chunk boundary). "stale" means the user
  // picked a new date range meanwhile; "failed" means an error was shown.
  const loadEverything = useCallback(
    async (tab: HotelPastTab): Promise<"complete" | "failed" | "stale"> => {
      const generation = generationRef.current;
      for (let attempt = 0; attempt < 5; attempt++) {
        if (generationRef.current !== generation) return "stale";
        if (!tabsDataRef.current[tab].cursor) return "complete";
        const ok = await loadRemaining(tab, Number.MAX_SAFE_INTEGER);
        if (!ok) return "failed";
      }
      if (generationRef.current !== generation) return "stale";
      return tabsDataRef.current[tab].cursor ? "failed" : "complete";
    },
    [loadRemaining]
  );

  const isMealTab = activeTab === TABS.MEAL_SCANS;
  const activeData = tabsData[activeTab];
  const isActiveTabLoading = loadingTabs[activeTab];
  const isFiltering = !!searchParams.trim() || !!reviewStatusFilter;

  // Search and the review filter run in the browser, so they need the whole
  // range. As soon as either is used, fetch whatever is still on the server;
  // results already loaded show right away and keep growing.
  useEffect(() => {
    if (isFiltering && !isRowsLoading && activeData.cursor) {
      loadEverything(activeTab);
    }
  }, [isFiltering, isRowsLoading, activeData.cursor, activeTab, loadEverything]);

  const handleLoadMore = () => {
    loadRemaining(activeTab, LOAD_CHUNK_ROWS);
  };

  // The export always covers the whole date range: any rows not loaded yet
  // are fetched first, then the complete, unfiltered set is written out.
  const handleExport = async (extension: "csv" | "xlsx") => {
    if (isExporting) return;
    const tab = activeTab;
    setExporting(true);
    try {
      const result = await loadEverything(tab);
      if (result === "stale") return;
      if (result === "failed") {
        toast.error(
          "Export cancelled: not every scan in the range could be loaded. Please try again."
        );
        return;
      }
      const fileName = `Past_Hotel_${
        tab === TABS.MEAL_SCANS ? "Meal_Scans" : "CheckIn_Scans"
      }_Report_${moment().format("MMMM-D-YYYY")}.${extension}`;
      downloadCsv(
        tabsDataRef.current[tab].rows,
        tab === TABS.MEAL_SCANS ? MEAL_EXPORT_COLUMNS : CHECK_IN_EXPORT_COLUMNS,
        fileName
      );
    } catch (err: any) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Dates can be picked freely; Apply stays disabled while the draft range is
  // invalid, and the toolbar explains what is wrong.
  const dateError = useMemo(() => {
    const yesterday = moment(addDays(new Date(), -1)).endOf("day");
    if (
      moment(draftStartDate).isAfter(yesterday) ||
      moment(draftEndDate).isAfter(yesterday)
    ) {
      return "Dates must be in the past (up to yesterday).";
    }
    if (moment(draftStartDate).isAfter(draftEndDate, "day")) {
      return "Start date must be on or before the end date.";
    }
    if (differenceInDays(draftEndDate, draftStartDate) > MAX_RANGE_DAYS) {
      return "Date range cannot exceed 15 days.";
    }
    return "";
  }, [draftStartDate, draftEndDate]);

  const applyDisabled =
    !!dateError ||
    (moment(draftStartDate).isSame(appliedStartDate, "day") &&
      moment(draftEndDate).isSame(appliedEndDate, "day"));

  const handleApplyDates = () => {
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
  };

  // Picking either date slides the other one along so the range always stays
  // a valid window of at most 15 past days — no manual two-step adjusting.
  const handleDraftStartDate = (date: Date) => {
    const yesterday = addDays(new Date(), -1);
    const start = date > yesterday ? yesterday : date;
    setDraftStartDate(start);
    if (
      draftEndDate < start ||
      differenceInDays(draftEndDate, start) > MAX_RANGE_DAYS
    ) {
      const end = addDays(start, MAX_RANGE_DAYS);
      setDraftEndDate(end > yesterday ? yesterday : end);
    }
  };

  const handleDraftEndDate = (date: Date) => {
    const yesterday = addDays(new Date(), -1);
    const end = date > yesterday ? yesterday : date;
    setDraftEndDate(end);
    if (
      draftStartDate > end ||
      differenceInDays(end, draftStartDate) > MAX_RANGE_DAYS
    ) {
      setDraftStartDate(addDays(end, -MAX_RANGE_DAYS));
    }
  };

  const checkInColumns = useMemo(
    () => [
      { field: "tmId", headerName: "Crew Id", flex: 1 },
      {
        field: "FULL_NAME",
        headerName: "Full name",
        flex: 1.2,
        valueGetter: (value: any, row: any) => getCheckInFullName(row),
      },
      {
        field: "arrivalDate",
        headerName: "Check-in date",
        flex: 1.2,
        valueGetter: (value: any, row: any) => getCheckInDate(row),
      },
      {
        field: "empName",
        headerName: "Scanned by",
        flex: 1,
        valueGetter: (value: any, row: any) => getScannedByLabel(row),
      },
      {
        field: "scannedByGreeterOn",
        headerName: "Scanned on",
        flex: 1.2,
        valueGetter: (value: any, row: any) =>
          formatScanTimestamp(row.scannedByGreeterOn),
      },
      {
        // Comes from the manifest, so manual scans have no reservation number.
        field: "ReservationNo",
        headerName: "Reservation No",
        flex: 1,
        valueGetter: (value: any, row: any) => row.ReservationNo || "",
      },
      reviewStatusColumn,
    ],
    []
  );

  const mealScanColumns = useMemo(
    () => [
      { field: "tmId", headerName: "Crew Id", flex: 1 },
      {
        field: "TMDetails_Name",
        headerName: "Full name",
        flex: 1.2,
        valueGetter: (value: any, row: any) => row.TMDetails_Name || "",
      },
      { field: "date", headerName: "Date", flex: 1 },
      {
        field: "requestType",
        headerName: "Meal",
        flex: 1,
        valueGetter: (value: any, row: any) =>
          MEAL_TYPE_LABELS[row.requestType] || row.requestType || "",
      },
      {
        field: "CheckInDate",
        headerName: "Scanned on",
        flex: 1.2,
        valueGetter: (value: any, row: any) =>
          formatScanTimestamp(row.CheckInDate, "MM/DD/YYYY HH:mm:ss"),
      },
      {
        field: "vendorEmpName",
        headerName: "Scanned by",
        flex: 1,
        valueGetter: (value: any, row: any) =>
          row.isManuallyAdded ? "Manual Scan" : row.vendorEmpName || "",
      },
      reviewStatusColumn,
    ],
    []
  );

  const rowData = useMemo(() => {
    let data = activeData.rows;
    if (reviewStatusFilter) {
      data = data.filter(
        (item: any) => getReviewStatus(item) === reviewStatusFilter
      );
    }
    if (searchParams) {
      const needle = searchParams.toLowerCase();
      data = data.filter((item: any) => {
        return (
          (item.tmId || "").toLowerCase().includes(needle) ||
          (item.TMDetails_Name?.toLowerCase() || "").includes(needle) ||
          (item.FirstName?.toLowerCase() || "").includes(needle) ||
          (item.LastName?.toLowerCase() || "").includes(needle) ||
          (item.vendorEmpName?.toLowerCase() || "").includes(needle)
        );
      });
    }
    return data;
  }, [searchParams, reviewStatusFilter, activeData.rows]);

  const loadedCount = activeData.rows.length;
  const hasMore = activeData.cursor != null;
  // Without a count from the server, "2,000+" still tells the user there is more.
  const totalLabel =
    activeData.total != null
      ? formatCount(activeData.total)
      : `${formatCount(loadedCount)}${hasMore ? "+" : ""}`;

  const summaryText = useMemo(() => {
    const loaded = formatCount(loadedCount);
    const matches = formatCount(rowData.length);

    if (isFiltering) {
      return isActiveTabLoading
        ? `Found ${matches} so far · searching all scans in the range (${loaded} of ${totalLabel} checked)`
        : `Found ${matches} matching of ${loaded} scans`;
    }
    if (isActiveTabLoading) {
      return `Loading more scans… ${loaded} of ${totalLabel} loaded`;
    }
    return hasMore
      ? `Showing ${loaded} of ${totalLabel} scans · search, filter or load more to see the rest`
      : `Showing all ${loaded} scans`;
  }, [
    loadedCount,
    rowData.length,
    isFiltering,
    isActiveTabLoading,
    hasMore,
    totalLabel,
  ]);

  const loadProgress =
    activeData.total && activeData.total > 0
      ? Math.min(100, (loadedCount / activeData.total) * 100)
      : undefined;

  return (
    <div className="grid-section">
      <Tabs
        value={activeTab}
        onChange={(_event, newValue: HotelPastTab) => setActiveTab(newValue)}
        sx={{ marginTop: 1 }}
      >
        <Tab label="Check-in scans" value={TABS.CHECK_INS} />
        <Tab label="Meal scans" value={TABS.MEAL_SCANS} />
      </Tabs>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 12,
          fontSize: 13,
          color: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <span>{isRowsLoading ? "Loading scans…" : summaryText}</span>
        {!isRowsLoading && hasMore && !isActiveTabLoading && !isFiltering && (
          <MGButton variant="outlined" size="small" onClick={handleLoadMore}>
            Load more
          </MGButton>
        )}
      </div>
      {!isRowsLoading && isActiveTabLoading && (
        <LinearProgress
          variant={loadProgress != null ? "determinate" : "indeterminate"}
          value={loadProgress}
          sx={{ marginTop: 1, borderRadius: 1 }}
        />
      )}
      <DataGrid
        rows={rowData || []}
        // Fixed height keeps row virtualization on, so thousands of scans
        // render smoothly; the footer pages through them.
        sx={{ height: 650, marginTop: 1.5 }}
        columns={isMealTab ? mealScanColumns : checkInColumns}
        loading={isRowsLoading}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialState={{
          pagination: { paginationModel: { pageSize: 50, page: 0 } },
        }}
        disableRowSelectionOnClick
        slots={{
          toolbar: HotelPastToolbar,
        }}
        slotProps={
          {
            toolbar: {
              searchParams,
              handleSearchParam: setSearchParams,
              startDate: draftStartDate,
              endDate: draftEndDate,
              handleStartDate: handleDraftStartDate,
              handleEndDate: handleDraftEndDate,
              onApplyDates: handleApplyDates,
              applyDisabled,
              dateError,
              reviewStatusFilter,
              handleReviewStatusFilter: setReviewStatusFilter,
              onExport: handleExport,
              isExporting,
            },
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "skeleton",
            },
          } as any
        }
      />
    </div>
  );
};
